import { useRouteMatch } from 'react-router';

import { Button } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { PageProps } from '@/types/PageProps';

import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';

import ApplicationList from '@/components/Operational/Application/ApplicationList';
import ApplicationModal from '@/components/Operational/Application/ApplicationModalForm';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { SaveButton } from '@/components/_common';
import { useTaskFormContext } from '../../TasksProvider';
import {
  CreateTaskApplicationRequest,
  TaskApplicationFormValues,
} from '@/core/domain/operational/TaskAppllication/TaskApplication.types';
import { useCreateTaskApplication } from '@/core/domain/operational/TaskAppllication/TaskApplication.hooks';
import {
  useBrandMutation,
  useModelMutation,
} from '@/core/domain/people/BrandsModelsVersions/BrandsModelsVersions.hooks';
import { useDisclosure } from '@/hooks';
import TaskFooter from '@/components/Operational/Tasks/TaskFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import AccessDenied from '@/components/_common/AccessDenied';

function TaskApplicationsPage({ route }: PageProps) {
  const intl = useIntl().formatMessage;
  const [modalVisible, modalVisibleAction] = useDisclosure();
  const { taskId } = useRouteMatch<{ taskId: string }>().params;
  const { handleSubmit, disabled, isLoading } = useTaskFormContext();
  const createMutation = useCreateTaskApplication();
  const brandMutation = useBrandMutation();
  const modelMutation = useModelMutation();

  const handleCreateTaskApplication = async (
    formValues: TaskApplicationFormValues,
  ) => {
    if (formValues) {
      const brand = await brandMutation.mutateAsync(formValues.brandId);
      const model = await modelMutation.mutateAsync(formValues.modelId);

      const obj: CreateTaskApplicationRequest = {
        model: {
          brand: {
            brandId: formValues.brandId,
            name: brand.name,
          },
          modelId: formValues.modelId,
          name: model.name,
        },
        taskId: Number(taskId),
        applicationExecutionTime: formValues.applicationExecutionTime,
      };

      await createMutation.mutateAsync(obj);

      modalVisibleAction.close();
    }
  };

  return (
    <>
      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        baseKey={taskId}
        basePath={`/app/operational/tasks/${taskId}`}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={modalVisibleAction.open}
              disabled={
                !hasPermissions([Authorities.REGISTER_TASK_ADD_APPLICATION])
              }
            >
              <FormattedMessage id="task.application.vehicles.create" />
            </Button>

            <SaveButton
              onClick={handleSubmit}
              disabled={
                disabled || !hasPermissions([Authorities.REGISTER_TASK_EDIT])
              }
              block
              loading={isLoading}
            >
              {intl({ id: 'commons.entity.save' })}
            </SaveButton>
          </SpaceForButton>
        }
      >
        {!hasPermissions([Authorities.REGISTER_TASK_VIEW_APPLICATION]) ? (
          <AccessDenied />
        ) : (
          <>
            <ApplicationList taskId={Number(taskId)} />

            <TaskFooter disabled={disabled} />
          </>
        )}

        <ApplicationModal
          onOk={handleCreateTaskApplication}
          visible={modalVisible}
          handleVisible={modalVisibleAction.toggle}
          isLoading={
            createMutation.isLoading ||
            brandMutation.isLoading ||
            modelMutation.isLoading
          }
        />
      </DefaultRouteTabs>
    </>
  );
}

export default TaskApplicationsPage;
